import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  LocationScope,
  TopicIndicator,
  TopicIndicatorDetail,
  TopicIndicatorEdge,
  useDeviceDetailsQuery,
  useRoomQuery,
  useSiteOverviewQuery
} from "generated/graphql";
import i18next, { LocalKeys, useTranslation } from "utilities/i18n";
import { getInsightTitleTranslation } from "utilities/insights";
import { handleNullFields } from "utilities/stringUtils";
import { Link } from "polyrouter";
import { tMetric } from "collections/InsightDetails";
import {
  InsightFilterState,
  usePriorityFilter
} from "../../Priority/QueryParams";
import { generatePath, useRouteMatch } from "react-router";
import { NoResultsIndicator, ShimmerLink } from "elements";
import { TableHeaderItem } from "collections/Table";

const tableColumns = ({
  data,
  tM = () => {},
  setDeleted,
  deleted
}: {
  data: TopicIndicator;
  tM?: any;
  setDeleted?: Dispatch<SetStateAction<boolean>>;
  deleted?: boolean;
}) => {
  const insightDetails = generatePath("/insights/details/:id", { id: data.id });
  const indicatorName = getInsightTitleTranslation(data.topic.id);
  const indicatorKey =
    data.indicator?.__typename !== "AverageIndicator"
      ? `Total ${indicatorName
          .replace(/\( *minutes *\/ *day *\)/gi, "")
          .toLowerCase() || ""}`
      : indicatorName || "";

  const insightColumnHeader = i18next.t(indicatorKey as LocalKeys);
  switch (data.locationScope) {
    case LocationScope.Room:
      return [
        {
          key: "room-name",
          label: i18next.t("ROOM"),
          indicator: !data.room,
          content: (
            <RoomLink data={data} setDeleted={setDeleted} deleted={deleted} />
          )
        },
        {
          key: "insight-data",
          label: insightColumnHeader,
          indicator: !data.indicator,
          content: tM(data.indicator, true)
        }
      ];
    case LocationScope.Site:
      return [
        {
          key: "site-name",
          label: i18next.t("SITE"),
          indicator: !data.site,
          content: (
            <SiteLink data={data} deleted={deleted} setDeleted={setDeleted} />
          )
        },
        {
          key: "insight-data",
          label: insightColumnHeader,
          indicator: !data.indicator,
          content: tM(data.indicator, true)
        }
      ];
    case LocationScope.Family:
      return [
        {
          key: "family",
          label: i18next.t("FAMILY"),
          indicator: !data.family,
          content: handleNullFields(data.family)
        },
        {
          key: "insight-data",
          label: insightColumnHeader,
          indicator: !data.indicator,
          content: <Link to={insightDetails}>{tM(data.indicator, true)}</Link>
        }
      ];
    case LocationScope.Model:
      return [
        {
          key: "model",
          label: i18next.t("DEVICE_MODEL"),
          indicator: !data.model,
          content: handleNullFields(data.model)
        },
        {
          key: "insight-data",
          label: insightColumnHeader,
          indicator: !data.indicator,
          content: <Link to={insightDetails}>{tM(data.indicator, true)}</Link>
        }
      ];
    default:
      return [
        {
          key: "device-name",
          label: i18next.t("DEVICE_NAME"),
          indicator: !data.device,
          // TODO: Get rid of manual lowercasing when Saga bug is fixed
          content: (
            <DeviceLink data={data} setDeleted={setDeleted} deleted={deleted} />
          )
        },
        {
          key: "insight-data",
          label: insightColumnHeader,
          indicator: !data.indicator,
          content: tM(data.indicator, true)
        },
        {
          key: "mac",
          label: i18next.t("MAC_ADDRESS"),
          indicator: !data.device,
          content: handleNullFields(data.device?.id)
        },
        {
          key: "room-name",
          label: i18next.t("ROOM"),
          indicator: !data.room,
          content: handleNullFields(data.room?.name)
        },
        {
          key: "site-name",
          label: i18next.t("SITE"),
          indicator: !data.site,
          content: handleNullFields(data.site?.name)
        },
        {
          key: "model",
          label: i18next.t("DEVICE_MODEL"),
          indicator: !data.model,
          content: handleNullFields(data.model)
        },
        {
          key: "software-version",
          label: i18next.t("SOFTWARE_VERSION"),
          indicator: !data.version,
          content: handleNullFields(data.version)
        }
      ];
  }
};

const SiteLink: React.FC<{
  data: Partial<TopicIndicator>;
  setDeleted?: Dispatch<SetStateAction<boolean>>;
  deleted?: boolean;
}> = ({ data, setDeleted, deleted }) => {
  const { url } = useRouteMatch();
  const { data: siteData, fetching } = useSiteOverviewQuery({
    variables: {
      id: data.site?.id || ""
    }
  });
  useEffect(() => {
    if (!deleted && setDeleted && !fetching && !siteData?.getSite) {
      setDeleted(true);
    }
  }, [deleted, setDeleted, siteData, fetching]);
  return (
    <ShimmerLink
      persist={["InsightsFilterState"]}
      loading={fetching}
      hasData={!!siteData?.getSite}
      link={generatePath(url + "/insight-site/:siteId", {
        siteId: handleNullFields(data.site?.id.toLowerCase())
      })}
      display={data.site?.name}
    />
  );
};

const RoomLink: React.FC<{
  data: Partial<TopicIndicator>;
  setDeleted?: Dispatch<SetStateAction<boolean>>;
  deleted?: boolean;
}> = ({ data, setDeleted, deleted }) => {
  const { url } = useRouteMatch();
  const { data: roomData, fetching } = useRoomQuery({
    variables: {
      id: data.room?.id || ""
    }
  });
  useEffect(() => {
    if (!deleted && setDeleted && !fetching && !roomData?.room) {
      setDeleted(true);
    }
  }, [deleted, setDeleted, roomData, fetching]);
  return (
    <ShimmerLink
      persist={["InsightsFilterState"]}
      loading={fetching}
      hasData={!!roomData?.room}
      link={generatePath(url + "/insight-room/:roomId", {
        roomId: handleNullFields(data.room?.id.toLowerCase())
      })}
      display={data.room?.name}
    />
  );
};

const DeviceLink: React.FC<{
  data: Partial<TopicIndicator>;
  setDeleted?: Dispatch<SetStateAction<boolean>>;
  deleted?: boolean;
}> = ({ data, setDeleted, deleted }) => {
  const { url } = useRouteMatch();
  const { data: device, fetching } = useDeviceDetailsQuery({
    variables: {
      filter: {
        AND: [
          {
            field: "id",
            eq: data.device?.id
          },
          {
            field: "tenantId",
            eq: data.tenant?.id
          }
        ]
      }
    }
  });
  useEffect(() => {
    if (!deleted && setDeleted && !fetching && !device?.devices.length) {
      setDeleted(true);
    }
  }, [deleted, setDeleted, device, fetching]);
  return (
    <ShimmerLink
      persist={["InsightsFilterState"]}
      loading={fetching}
      hasData={!!device?.devices.length}
      link={generatePath(url + "/insight-device/:deviceId", {
        deviceId: handleNullFields(data.device?.id.toLowerCase())
      })}
      display={data.device?.name}
    />
  );
};

const InsightBreakdownList: React.FC<{
  data: Partial<TopicIndicatorDetail> | null;
}> = ({ data }) => {
  const { t } = useTranslation();
  const [filterState, setFilter] = usePriorityFilter();
  const [deleted, setDeleted] = useState(false);

  useEffect(() => {
    if (
      data &&
      data.locationScope &&
      filterState.sort &&
      !columns.filter(entry => entry.key === filterState.sort?.field).length
    ) {
      // This logic checks whether the selected sort field actually exists as a table column, and clears the sort field param if not
      setFilter({
        ...filterState,
        sort: undefined
      });
    }
  });

  if (!data || !data.connection || !data.connection.edges.length) {
    return <NoResultsIndicator />;
  }

  const columns = tableColumns({ data: data.connection.edges[0].node });
  return (
    <>
      {deleted && (
        <div className="mb-1 d-flex greyed-out notice">
          <div style={{ fontWeight: "bold", color: "inherit" }}>
            {t("NOTE")}&nbsp;
          </div>
          {t("DELETED_DEVICES")}
        </div>
      )}
      <table className="table mb-2">
        <thead>
          <tr>
            {columns.map(({ key, label }) => (
              <TableHeaderItem
                fieldName={key}
                title={label}
                className={`insight-${key}`}
                sort={{
                  currentSort: filterState.sort ?? {
                    field: "insight-data",
                    direction: "DESC"
                  },
                  onSort: sort => {
                    setFilter({
                      ...filterState,
                      sort
                    });
                  }
                }}
                key={key}
                dataTestId={`insight-${key}`}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {data.connection.edges
            .sort(sortBreakdown(filterState))
            .map((entry, index) => (
              <InsightBreakdownRow
                key={entry.node.id + index}
                data={entry.node}
                setDeleted={setDeleted}
                deleted={deleted}
              />
            ))}
        </tbody>
      </table>
    </>
  );
};

const sortBreakdown = (filterState: InsightFilterState) => (
  first: TopicIndicatorEdge,
  second: TopicIndicatorEdge
) => {
  const direction =
    filterState.sort && filterState.sort.direction === "ASC" ? 1 : -1;
  switch (filterState.sort?.field) {
    case "device-name":
      return (
        direction *
        handleNullFields(first.node.device?.name).localeCompare(
          handleNullFields(second.node.device?.name)
        )
      );
    case "mac":
      return (
        direction *
        handleNullFields(first.node.device?.id).localeCompare(
          handleNullFields(second.node.device?.id)
        )
      );
    case "site-name":
      return (
        direction *
        handleNullFields(first.node.site?.name).localeCompare(
          handleNullFields(second.node.site?.name)
        )
      );
    case "room-name":
      return (
        direction *
        handleNullFields(first.node.room?.name).localeCompare(
          handleNullFields(second.node.room?.name)
        )
      );
    case "model":
      return (
        direction *
        handleNullFields(first.node.model).localeCompare(
          handleNullFields(second.node.model)
        )
      );
    case "software-version":
      return (
        direction *
        handleNullFields(first.node.version).localeCompare(
          handleNullFields(second.node.version)
        )
      );
    case "family":
      return (
        direction *
        handleNullFields(first.node.family).localeCompare(
          handleNullFields(second.node.family)
        )
      );
    default:
      if (first.node.indicator.__typename === "AverageIndicator") {
        if (second.node.indicator.__typename === "AverageIndicator") {
          return (
            direction *
            (first.node.indicator.average - second.node.indicator.average)
          );
        }
      } else if (second.node.indicator.__typename !== "AverageIndicator") {
        return (
          direction * (first.node.indicator.count - second.node.indicator.count)
        );
      }
      return 0;
  }
};

export const InsightBreakdownRow: React.FC<{
  data: TopicIndicator;
  setDeleted: Dispatch<SetStateAction<boolean>>;
  deleted: boolean;
}> = ({ data, setDeleted, deleted }) => {
  const tM = tMetric();

  const columns = tableColumns({ data, tM, setDeleted, deleted });

  return (
    <tr data-testid="insight-table-row">
      {columns.map(({ key, content }) => {
        return (
          <td key={key} className={`insight-${key}`}>
            {content}
          </td>
        );
      })}
    </tr>
  );
};

export default InsightBreakdownList;
